import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { Box } from "@mui/material";
import * as d3 from "d3";

const GraphWrapper = styled(Box)(`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & h4 {
    width: 100%;
    color: #000;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    padding: 10px;
  }
`);

const colorArray = [
  { dark: "#535bc4", main: "#6872f5", light: "#f0edff" },
  { dark: "#2a7bbe", main: "#349aee", light: "#e7f4ff" },
  { dark: "#1c94a7", main: "#23b9d1", light: "#e1f6f9" },
  { dark: "#9b42b1", main: "#c253dd", light: "#f8e9fc" },
  { dark: "#c5396a", main: "#f64784", light: "#ffebf2" }
];

const config = {
  innerRadius: 57,
  outerRadius: 145,
  darkSegmentHeight: 27,
  startAngle: -77,
  endAngle: 77
};

const CircularDashboard = ({ rawData = [], heading }) => {
  const svgRef = useRef(null);

  const activeData = (rawData || []).map((d, i) => ({
    ...d,
    ...colorArray[i % colorArray.length]
  }));

  useEffect(() => {
    const width = 380;
    const height = 180;

    const {
      innerRadius,
      outerRadius,
      darkSegmentHeight,
      startAngle,
      endAngle
    } = config;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height + 10})`);

    const x = d3
      .scaleBand()
      .range([
        (startAngle * Math.PI) / 180,
        (endAngle * Math.PI) / 180
      ])
      .domain(activeData.map((d) => d.name));

    const y = d3
      .scaleLinear()
      .domain([0, 100])
      .range([innerRadius, outerRadius]);

    // 1. Background
    svg
      .selectAll(".bg")
      .data(activeData)
      .enter()
      .append("path")
      .attr("class", "bg")
      .attr("fill", (d) => d.light)
      .attr(
        "d",
        d3
          .arc()
          .innerRadius(innerRadius)
          .outerRadius(outerRadius)
          .startAngle((d) => x(d.name))
          .endAngle((d) => x(d.name) + x.bandwidth())
          .padAngle(0.02)
      );

    // 2. Main Bar
    svg
      .selectAll(".main")
      .data(activeData)
      .enter()
      .append("path")
      .attr("class", "main")
      .attr("fill", (d) => d.main)
      .attr(
        "d",
        d3
          .arc()
          .innerRadius(innerRadius + darkSegmentHeight)
          .outerRadius((d) => y(d.value))
          .startAngle((d) => x(d.name))
          .endAngle((d) => x(d.name) + x.bandwidth())
          .padAngle(0.02)
      );

    // 3. Dark Segment
    svg
      .selectAll(".dark")
      .data(activeData)
      .enter()
      .append("path")
      .attr("class", "dark")
      .attr("fill", (d) => d.dark)
      .attr(
        "d",
        d3
          .arc()
          .innerRadius(innerRadius)
          .outerRadius(innerRadius + darkSegmentHeight)
          .startAngle((d) => x(d.name))
          .endAngle((d) => x(d.name) + x.bandwidth())
          .padAngle(0.02)
      );

    // 4. Percentage Text
    svg
      .selectAll(".pct")
      .data(activeData)
      .enter()
      .append("text")
      .attr("class", "pct")
      .attr("transform", (d) => {
        const angleDeg =
          ((x(d.name) + x.bandwidth() / 2) * 180) / Math.PI;
        return `rotate(${angleDeg - 90}) translate(${
          innerRadius + darkSegmentHeight / 2
        },0) rotate(90)`;
      })
      .text((d) => `${d.value}%`)
      .attr("fill", "#fff")
      .attr("font-size", "11px")
      .attr("font-weight", "bold")
      .attr("text-anchor", "middle")
      .attr("alignment-baseline", "middle");

    // 5. Labels
    svg
      .selectAll(".lbl")
      .data(activeData)
      .enter()
      .append("text")
      .attr("class", "lbl")
      .attr("transform", (d) => {
        const angleDeg =
          ((x(d.name) + x.bandwidth() / 2) * 180) / Math.PI;
        return `rotate(${angleDeg - 90}) translate(${
          outerRadius + 20
        },0) rotate(90)`;
      })
      .attr("text-anchor", "middle")
      .style("font-size", "11px")
      .each(function (d) {
        const text = d3.select(this);

        text
          .append("tspan")
          .attr("x", 0)
          .attr("dy", "-0.6em")
          .text(d.name?.[0] || "");

        text
          .append("tspan")
          .attr("x", 0)
          .attr("dy", "1.2em")
          .text(d.name?.[1] || "");
      });


      
// Circle + Direction Arrow Marker
// 6. Circle + Direction Arrow Marker
        svg
          .selectAll(".marker-group")
          .data(activeData)
          .enter()
          .append("g")
          .attr("class", "marker-group")
          .each(function (d) {
            if(d.direction === '') return false
            
            const group = d3.select(this);
            const angle = x(d.name) + x.bandwidth() / 2;
            const value = Math.max(d.value, 30);
            const radius = y(value) + 5;
            
            const cx = Math.cos(angle - Math.PI / 2) * radius;
            const cy = Math.sin(angle - Math.PI / 2) * radius;

            group
              .append("circle")
              .attr("cx", cx)
              .attr("cy", cy)
              .attr("r", 9)
              .attr("fill", d.main)
              .attr("stroke", "#fff")
              .attr("stroke-width", 1.5);

            const arrowLength = 8;
            const arrowWidth = 5;

            const angleDeg = angle * 180 / Math.PI;
            const rotation = angleDeg + (d.direction === "Up" ? 0 : 180);
            const rotationPosi = (d.direction === "Up" ? -2 : 2);

            const arrowPath = `
              M 0 -3.5
              L 5 2
              L -5 2
              Z
            `;

            group.append("path")
              .attr("class", "direction-arrow")
              .attr("fill", "#fff")
              .attr("d", arrowPath)
              .attr("transform", `translate(${cx},${cy}) rotate(${rotation})`);

      });
          
  }, [activeData]);

  return (
    <GraphWrapper>
      <svg ref={svgRef} />
      <h4>{heading}</h4>
    </GraphWrapper>
  );
};

export default CircularDashboard;